import { RadioTextFilterOperation } from '@hotjar/global-filters';
import type {
  CompletionFilterData,
  FilterData,
  KeywordFilterData,
  RatingFilterData,
  RecordingFilterData,
  UrlFilterData,
} from '../../FilterData';
import { CompletionOptions, RecordingOptions } from '../../FilterDataType';
import { parseNumber, undelimit } from '../../../../../common/Responses/Filters/Query/queryUtils';

function* parseUrl(params: URLSearchParams): Generator<FilterData> {
  const value = params.get('url');
  if (value === null || value.length === 0) return;
  yield {
    id: '1',
    type: 'url',
    value: { value, operation: RadioTextFilterOperation.IS },
  };
}

function* parseUrlStarts(params: URLSearchParams): Generator<UrlFilterData> {
  const value = params.get('url_starts');
  if (value === null || value.length === 0) return;
  yield {
    id: '1',
    type: 'url',
    value: {
      value,
      operation: RadioTextFilterOperation.STARTS_WITH,
    },
  };
}

function* parseUrlEnds(params: URLSearchParams): Generator<UrlFilterData> {
  const value = params.get('url_ends');
  if (value === null || value.length === 0) return;
  yield {
    id: '1',
    type: 'url',
    value: {
      value,
      operation: RadioTextFilterOperation.ENDS_WITH,
    },
  };
}

function* parseUrlContains(params: URLSearchParams): Generator<UrlFilterData> {
  const value = params.get('url_contains');
  if (value === null || value.length === 0) return;
  yield {
    id: '1',
    type: 'url',
    value: {
      value,
      operation: RadioTextFilterOperation.CONTAINS,
    },
  };
}

function* parseUrlNotContains(params: URLSearchParams): Generator<UrlFilterData> {
  const value = params.get('url_not_contains');
  if (value === null || value.length === 0) return;
  yield {
    id: '1',
    type: 'url',
    value: {
      value,
      operation: RadioTextFilterOperation.NOT_CONTAINS,
    },
  };
}

function* parseCompletion(params: URLSearchParams): Generator<CompletionFilterData> {
  const param = params.get('completion');
  if (param === null) return;

  const valueMap: Record<string, CompletionOptions> = {
    complete: CompletionOptions.COMPLETE,
    incomplete: CompletionOptions.INCOMPLETE,
    all: CompletionOptions.ALL,
  };

  const value = valueMap[param];
  if (value === undefined) return;

  yield {
    id: '2',
    type: 'completion',
    value,
  };
}

function* parseKeywordContains(params: URLSearchParams): Generator<KeywordFilterData> {
  const value = params.get('keyword_contains');
  if (value === null || value.length === 0) return;
  yield {
    id: '4',
    type: 'keyword',
    value: {
      value,
      operation: RadioTextFilterOperation.CONTAINS,
    },
  };
}

function* parseKeywordNotContains(params: URLSearchParams): Generator<KeywordFilterData> {
  const value = params.get('keyword_not_contains');
  if (value === null || value.length === 0) return;
  yield {
    id: '4',
    type: 'keyword',
    value: {
      value,
      operation: RadioTextFilterOperation.NOT_CONTAINS,
    },
  };
}

function* parseRating(params: URLSearchParams): Generator<RatingFilterData> {
  const param = params.get('rating');
  if (param === null) return;
  const values = new Set(
    undelimit(param)
      .map(parseNumber)
      .filter((num): num is number => num !== undefined),
  );
  yield {
    id: '5',
    type: 'reaction',
    value: Array.from(values),
  };
}

function* parseRecording(params: URLSearchParams): Generator<RecordingFilterData> {
  const param = params.get('recording');
  if (param === null) return;

  const valueMap: Record<string, RecordingOptions> = {
    captured: RecordingOptions.CAPTURED,
    not_captured: RecordingOptions.NOT_CAPTURED,
    all: RecordingOptions.ALL,
  };

  const value = valueMap[param];
  if (value === undefined) return;

  yield {
    id: '3',
    type: 'recording',
    value,
  };
}

function* getFilters(params: URLSearchParams): Generator<FilterData> {
  yield* parseUrl(params);
  yield* parseUrlStarts(params);
  yield* parseUrlEnds(params);
  yield* parseUrlContains(params);
  yield* parseUrlNotContains(params);
  yield* parseCompletion(params);
  yield* parseRecording(params);
  yield* parseKeywordContains(params);
  yield* parseKeywordNotContains(params);
  yield* parseRating(params);
}

export const convertQueryToLocalFilters = (params: URLSearchParams): FilterData[] => {
  const localFilters = [...getFilters(params)];

  return localFilters;
};
